"use client"

import { useState, useRef, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import IconComponent from "@/helpers/functions/getIcon";
import Image from "next/image";

export default function Interface({
  screenshots = [],
  screenshotsMobile = [],
  className,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [activeTab, setActiveTab] = useState("desktop");
  const containerRef = useRef(null);

  const currentScreenshots = activeTab === "desktop" ? screenshots : screenshotsMobile;
  const currentLength = currentScreenshots.length;

  const nextSlide = () => {
    if (animating) return;
    setAnimating(true);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % currentLength);
    setTimeout(() => setAnimating(false), 300);
  };

  const prevSlide = () => {
    if (animating) return;
    setAnimating(true);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + currentLength) % currentLength);
    setTimeout(() => setAnimating(false), 300);
  };

  useEffect(() => {
    // Reset currentIndex when the active tab changes
    setCurrentIndex(0);
  }, [activeTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setCurrentIndex(0); // Reset currentIndex on tab change
  };

  const prevIndex = (currentIndex - 1 + currentLength) % currentLength;
  const nextIndex = (currentIndex + 1) % currentLength;

  return (
    <div className={cn("w-full max-w-5xl mx-auto", className)}>
      <Tabs
        defaultValue="desktop"
        className="w-full"
      >
        <div className="flex items-center justify-between mb-2 gap-1">
          <TabsList className="p-1 bg-card-foreground m-0">
            <TabsTrigger 
              value="desktop" 
              className="flex items-center gap-2 duration-300 hover:bg-white/5"
              onClick={() => handleTabChange("desktop")}
            >
              <IconComponent iconName={'laptop'} className={'!text-sm text-blue'} />
              <span>Desktop</span>
            </TabsTrigger>
            <TabsTrigger 
              value="mobile" 
              className="flex items-center ml-1 gap-2 duration-300 hover:bg-white/5"
              onClick={() => handleTabChange("mobile")}
            >
              <IconComponent iconName={'smartphone'} className={'!text-sm text-blue'}  />
              <span>Mobile</span>
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <span className="text-xs py-1 text-white/70 px-1 rounded-sm shadow-md text-muted-foreground">
              {currentIndex + 1} / {currentLength}
            </span>
            <div className="flex items-center">
              <button
                variant="outline"
                size="icon"
                onClick={prevSlide}
                className="rounded-r-none rounded-l-md flex items-center py-1 px-2 bg-card hover:bg-card-foreground duration-300 hover:text-blue leading-none"
                aria-label="Previous image"
                disabled={animating}
              >
                <IconComponent iconName={'chevron_left'} className={"!text-xl"} />
              </button>
              <button
                variant="outline"
                size="icon"
                onClick={nextSlide}
                className="rounded-l-none border-l-0 rounded-r-md py-1 px-2 bg-card duration-300 hover:bg-card-foreground hover:text-blue ml-1 flex items-center leading-none"
                aria-label="Next image"
                disabled={animating}
              >
                <IconComponent iconName={'chevron_right'} className={"!text-xl"} />
              </button>
            </div>
          </div>
        </div>

        <TabsContent value="desktop" className="mt-0">
          {/* Desktop content */}
          <div className="relative mx-auto border-[2px] p-2 lg:p-6 bg-black/35 border-white/10 rounded-2xl">
            <div className="bg-white/10 rounded-t-lg pt-2 lg:pt-3 lg:pb-2 pb-1 px-4 shadow-xl relative">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-full max-w-[100px] lg:h-5 h-4 bg-black/65 rounded-b-md"></div>
              <div className="flex items-center justify-start gap-1.5 mb-1">
                <div className="lg:w-3 lg:h-3 w-2 h-2 rounded-full bg-red"></div>
                <div className="lg:w-3 lg:h-3 w-2 h-2 rounded-full bg-yellow"></div>
                <div className="lg:w-3 lg:h-3 w-2 h-2 rounded-full bg-green"></div>
              </div>
            </div>
            <div className="bg-zinc-800 border-[1px] border-white/10   rounded-b-lg shadow-xl">
              <div
                ref={containerRef}
                className="relative bg-card overflow-hidden rounded cursor-grab active:cursor-grabbing"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <Image
                    src={process.env.NEXT_PUBLIC_CDN_URL +currentScreenshots[prevIndex]?.directus_files_id.filename_disk || "/placeholder.svg"}
                    alt={currentScreenshots[prevIndex]?.directus_files_id.description || "Interface"}
                    width={500}
                    height={300}
                    className={cn(
                      "w-full h-full object-fit absolute inset-0 transition-transform duration-300",
                      "translate-x-[-100%]",
                    )}
                  />

                  <Image
                    src={process.env.NEXT_PUBLIC_CDN_URL + currentScreenshots[currentIndex]?.directus_files_id.filename_disk || "/placeholder.svg"}
                    alt={currentScreenshots[currentIndex]?.directus_files_id.description || "Interface"}
                    width={500}
                    height={300}
                    className={cn(
                      "w-full h-full object-fit absolute inset-0 transition-transform duration-300",
                    )}
                  />

                  <Image
                    src={process.env.NEXT_PUBLIC_CDN_URL + currentScreenshots[nextIndex]?.directus_files_id.filename_disk || "/placeholder.svg"}
                    alt={currentScreenshots[nextIndex]?.directus_files_id.description || "Interface"}
                    width={500}
                    height={300}
                    className={cn(
                      "w-full h-full object-fit absolute inset-0 transition-transform duration-300",
                      "translate-x-[100%]",
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="mobile" className="mt-0">
        <div className="relative mx-auto max-w-[280px]">
  <div className="bg-white/10 rounded-[1.5rem] pt-4 shadow-xl border-4 border-black/50 relative ">
    {/* Top Notch */}
    <div className="absolute top-2.5 left-1/2 transform -translate-x-1/2 w-full max-w-[70px] h-5 bg-black rounded-xl"></div>
    
    {/* Status Bar */}
    <div className="flex justify-between items-center text-white text-xs px-2 py-2 absolute top-1 left-0 right-0">
      <span className="ml-4 font-medium text-[10px] flex items-center gap-1"><div>15:41</div>  <IconComponent className={"!text-[11px]"} iconName={'dark_mode'} /></span>
      <div className="flex items-center leading-none gap-1.5">
        <div className="flex items-end gap-0.5">
            <div className="h-[4px] w-[3px] rounded-sm bg-white"></div>
            <div className="h-[6px] w-[3px] rounded-sm bg-white"></div>
            <div className="h-[8px] w-[3px] rounded-sm bg-white/50"></div>
            <div className="h-[10px] w-[3px] rounded-sm bg-white/50"></div>
        </div>
        <span className=" text-[10px] leading-none">5G</span>
        <div className="bg-green  h-3 text-[7px] py-1 font-medium px-1 rounded-sm mr-2 flex items-center">100</div>
      </div>
    </div>

    {/* Screen */}
    <div className="mt-6 overflow-hidden   relative">
      <div
        ref={containerRef}
        className="aspect-[9/19.5] overflow-hidden bg-card relative cursor-grab active:cursor-grabbing rounded-b-[1.5rem]"
      >
        <Image
          src={process.env.NEXT_PUBLIC_CDN_URL + currentScreenshots[prevIndex]?.directus_files_id.filename_disk || "/placeholder.svg"}
          alt={currentScreenshots[prevIndex]?.directus_files_id.description || "Interface"}
          width={350}
          height={600}
          className={cn(
            "w-full h-full object-fit absolute inset-0 transition-transform duration-300",
            "translate-x-[-100%]",
          )}
        />

        <Image
          width={350}
          height={600}
          src={process.env.NEXT_PUBLIC_CDN_URL + currentScreenshots[currentIndex]?.directus_files_id.filename_disk || "/placeholder.svg"}
          alt={currentScreenshots[currentIndex]?.directus_files_id.description || "Interface"}
          className={cn(
            "w-full h-full object-fit absolute inset-0 transition-transform duration-300 ",
          )}
        />

        <Image
          width={350}
          height={600}
          src={process.env.NEXT_PUBLIC_CDN_URL + currentScreenshots[nextIndex]?.directus_files_id.filename_disk || "/placeholder.svg"}
          alt={currentScreenshots[nextIndex]?.directus_files_id.description || "Interface"}
          className={cn(
            "w-full h-full object-fit absolute inset-0 transition-transform duration-300 ",
            "translate-x-[100%]",
          )}
        />
      </div>
    </div>

    {/* Bottom Edge */}
    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-20 h-1 bg-black/60 rounded-full"></div>
  </div>
</div>
        </TabsContent>
      </Tabs>
    </div>
  );
}